import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Panel} from 'react-bootstrap';
import {Button, IconButton} from '@material-ui/core';
import {Edit as EditIcon, DeleteForever as DeleteForeverIcon} from '@material-ui/icons';
import {isEmpty} from 'lodash/lang';
import {CommonModal, DeletionConfirmBox, Modal} from 'components/common';
import {SecondaryNavigation} from 'components/Navigation';
import UserList from './UserList';
import UserUpdate from './UserUpdate';
import UserRoleChangeForm from './UserRoleChangeForm';
import SchoolList from '../School/SchoolList';
import ClassroomList from '../Classroom/ClassroomList';
import ReadingList from '../Reading/ReadingList';
import MessageForm from '../User/MessageForm';
import {deleteUser} from 'api/users';
import {fetchUser, setCurrentUser} from 'actions/users';
import {addFlashMessage} from 'actions/flash-message';
import {formatErrors} from 'actions/errors';
import {buildUserFullName, formatRole} from 'utils/common';
import * as permissions from 'utils/permissions';

class UserDetail extends React.Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };
  static propTypes = {
    user: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      updateModalShown: false,
      changeRoleModalShown: false,
      messageModalShown: false,
      reading: null,
    };
    this.fetchUser = this.fetchUser.bind(this);
    this.showModal = this.showModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.deleteUser = this.deleteUser.bind(this);
    this.handleReadingMessageModalOpen = this.handleReadingMessageModalOpen.bind(this);
  }

  componentDidMount() {
    this.fetchUser();
  }

  componentWillUnmount() {
    this.props.setCurrentUser(null, {});
  }

  fetchUser() {
    const {
      fetchUser,
      formatErrors,
      match: {
        params: {userId},
      },
    } = this.props;
    fetchUser(userId).catch((err) => formatErrors(err, '/users'));
  }

  showModal(type) {
    this.setState({
      [`${type}ModalShown`]: true,
    });
  }

  closeModal() {
    this.setState({
      updateModalShown: false,
      changeRoleModalShown: false,
      messageModalShown: false,
    });
  }

  handleReadingMessageModalOpen({reading}) {
    this.setState({
      messageModalShown: true,
      reading: reading,
    });
  }

  deleteUser() {
    const {
      addFlashMessage,
      formatErrors,
      match: {
        params: {userId},
      },
    } = this.props;
    deleteUser(userId)
      .then(() => {
        addFlashMessage('The user has been successfully deleted!', 'success');
        this.context.router.history.push('/users');
      })
      .catch((err) => formatErrors(err));
  }

  render() {
    const {updateModalShown, changeRoleModalShown, messageModalShown, reading} = this.state;
    const {me, user} = this.props;
    if (isEmpty(user)) {
      return null;
    }
    return (
      <React.Fragment>
        <CommonModal title="Edit User" onClose={this.closeModal} show={updateModalShown}>
          <UserUpdate user={user} onSubmitSuccess={this.closeModal} />
        </CommonModal>
        <CommonModal title="Edit User Role" onClose={this.closeModal} show={changeRoleModalShown}>
          <UserRoleChangeForm
            user={user}
            onSubmitSuccess={() => {
              this.fetchUser();
              this.closeModal();
            }}
          />
        </CommonModal>
        <Modal
          title="Send a message"
          onClose={this.closeModal}
          open={messageModalShown}
          renderContent={() => <MessageForm receiver={user} reading={reading} onSubmitSuccess={this.closeModal} />}
        />
        <SecondaryNavigation hasBack />
        <div className="container content-box">
          <Panel>
            <h3 className="section-header">
              <div className="section-title">User Information</div>
              <div style={{display: 'flex', alignItems: 'center', flexFlow: 'row-reverse'}}>
                {permissions.canDeleteUser(me, user) && (
                  <DeletionConfirmBox
                    itemName="user"
                    deleteAction={this.deleteUser}
                    DeleteButton={(props) => (
                      <IconButton variant="contained" color="primary" {...props}>
                        <DeleteForeverIcon />
                      </IconButton>
                    )}
                  />
                )}
                {permissions.canEditUser(me, user) && (
                  <IconButton variant="contained" color="primary" onClick={() => this.showModal('update')}>
                    <EditIcon />
                  </IconButton>
                )}
                {me.id !== user.id && (
                  <Button variant="contained" color="primary" onClick={() => this.showModal('message')}>
                    Send a message
                  </Button>
                )}
              </div>
            </h3>
            <div className="table-block">
              <div className="table-row">
                <div className="table-cell">Email</div>
                <div className="table-cell">{user.email}</div>
              </div>
              <div className="table-row">
                <div className="table-cell">Username</div>
                <div className="table-cell">{user.username}</div>
              </div>
              <div className="table-row">
                <div className="table-cell">Name</div>
                <div className="table-cell">{buildUserFullName(user)}</div>
              </div>
              <div className="table-row">
                <div className="table-cell">Role</div>
                <div className="table-cell text-capitalize">
                  <span>{formatRole(user.role)}</span>
                  {permissions.isKluwellAdmin(me.role) && ['teacher', 'school_coordinator'].includes(user.role) && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => this.showModal('changeRole')}
                      className="small-margin"
                    >
                      Change Role
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Panel>
        </div>

        {permissions.isKluwellAdmin(me.role) ? (
          permissions.isSchoolCoordinatorOrTeacher(user.role) ? (
            <React.Fragment>
              <SchoolList user={user} />
              <ClassroomList user={user} />
              <UserList userId={user.id} userReaderOnly hiddenFields={['role']} />
            </React.Fragment>
          ) : permissions.isParent(user.role) ? (
            <React.Fragment>
              <SchoolList user={user} />
              <ClassroomList user={user} />
            </React.Fragment>
          ) : (
            permissions.isReader(user.role) && (
              <React.Fragment>
                <SchoolList user={user} />
                <ClassroomList user={user} />
                <ReadingList
                  userId={user.id}
                  onSendReadingMessageModalOpen={({reading}) => this.handleReadingMessageModalOpen({reading})}
                />
              </React.Fragment>
            )
          )
        ) : permissions.isSchoolCoordinatorOrTeacher(me.role) ? (
          permissions.isSchoolCoordinatorOrTeacher(user.role) ? (
            <React.Fragment>
              <ClassroomList user={user} />
              <UserList userId={user.id} userReaderOnly hiddenFields={['role']} />
            </React.Fragment>
          ) : permissions.isParent(user.role) ? (
            <React.Fragment>
              <SchoolList user={user} />
              <ClassroomList user={user} />
            </React.Fragment>
          ) : (
            permissions.isReader(user.role) && (
              <React.Fragment>
                <ClassroomList user={user} />
                <ReadingList
                  userId={user.id}
                  onSendReadingMessageModalOpen={({reading}) => this.handleReadingMessageModalOpen({reading})}
                />
              </React.Fragment>
            )
          )
        ) : (
          permissions.isParent(me.role) &&
          permissions.isReader(user.role) && (
            <React.Fragment>
              <ReadingList
                userId={user.id}
                onSendReadingMessageModalOpen={({reading}) => this.handleReadingMessageModalOpen({reading})}
              />
            </React.Fragment>
          )
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  const {account, users} = state;
  const me = account.get('user').toJS();
  const user = users.get('currentUser').toJS();
  return {
    me,
    user,
    isMe: me.id === user.id,
  };
};

const mapDispatchToProps = (dispatch) => ({
  addFlashMessage: (...args) => dispatch(addFlashMessage(...args)),
  formatErrors: (...args) => dispatch(formatErrors(...args)),
  fetchUser: (...args) => dispatch(fetchUser(...args)),
  setCurrentUser: (...args) => dispatch(setCurrentUser(...args)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserDetail);
